import {
  type BoxRegion,
  regionZoomScale,
  zoomDecision,
} from "components/Document/ReviewInteraction";
import { RefObject, useCallback, useEffect, useMemo, useRef } from "react";

import type { DocumentPreviewHighlight } from "./DocumentPreview";

import { visibleHeight } from "./visibleHeight";
import { clampZoom } from "./zoom";

/** Keeps the framed region in view across page jumps and zooms the pane applies asynchronously. */

/** Centring the outline, and knowing when it is safe to. */
interface CentredOutline {
  attach: (element: HTMLDivElement | null) => void;
  /** True once the page holding the frame has rendered. */
  isMounted: () => boolean;
  centre: (forFrame: string | null) => void;
  /** Say that a zoom was just asked for, so the centring after it is owed. */
  expectZoomCentre: (forFrame: string | null) => void;
  forget: () => void;
}

function useCentredOutline(frameKey: string | null): CentredOutline {
  /**
   * The framed region's own outline, once the page holding it has rendered.
   *
   * Scrolling the element rather than computing a target keeps this correct
   * through zoom: it sits at the region's position inside the page layer, so
   * wherever the pane has put the page, the element is where the box is.
   */
  const outlineRef = useRef<HTMLDivElement | null>(null);
  /**
   * The frame already centred, so this happens once per frame. The outline remounts
   * without the frame changing — a field sharing its region relabels it.
   */
  const centredRef = useRef<string | null>(null);
  /**
   * The frame owed a second centring once our own zoom has been applied.
   *
   * `zoomTo` re-lays out the page and the outline moves with it, but it does so
   * after the call returns — so centring in the same breath measures the box
   * where it used to be. Only ever set for a zoom we asked for: a reviewer's own
   * zoom is theirs, and pulling them back to the box on it is not help.
   */
  const owedRef = useRef<string | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const centre = useCallback((forFrame: string | null) => {
    const outline = outlineRef.current;
    if (outline == null) return;
    centredRef.current = forFrame;
    // Centred on both axes: a box in the right-hand column needs the horizontal
    // half as much as a box below the fold needs the vertical, and the pane
    // scrolls in both.
    //
    // Instant rather than smooth. A smooth scroll runs over many frames, which
    // is long enough for the pane's own scrolling — a page jump, a zoom — to
    // start midway and leave the reader between the two.
    outline.scrollIntoView({ block: "center", inline: "center", behavior: "auto" });
  }, []);

  const attach = useCallback(
    (element: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      outlineRef.current = element;
      if (element == null) return;

      // Pages are often laid out after the frame was set; centre once the outline
      // exists, unless this frame already has been.
      if (centredRef.current !== frameKey) centre(frameKey);

      if (typeof ResizeObserver === "undefined") return;
      // The resize is the only signal that a zoom has been laid out: setting the
      // zoom state returns before React has rendered it.
      const observer = new ResizeObserver(() => {
        const owed = owedRef.current;
        if (owed == null) return;
        owedRef.current = null;
        centre(owed);
      });
      observer.observe(element);
      observerRef.current = observer;
    },
    [frameKey, centre]
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const isMounted = useCallback(() => outlineRef.current != null, []);
  const expectZoomCentre = useCallback((forFrame: string | null) => {
    owedRef.current = forFrame;
  }, []);
  const forget = useCallback(() => {
    centredRef.current = null;
    owedRef.current = null;
  }, []);

  // Stable between frames: framing re-runs whenever this changes, and a re-run
  // on every render would pull the reader back to the box as they zoom or scroll.
  return useMemo(
    () => ({ attach, isMounted, centre, expectZoomCentre, forget }),
    [attach, isMounted, centre, expectZoomCentre, forget]
  );
}

export interface FramedRegion {
  /** The scrolling viewport, for measuring what a zoom has to fit into. */
  viewportRef: RefObject<HTMLDivElement | null>;
  /** Give this to the framed outline, and only that one. */
  attachFramedOutline: (element: HTMLDivElement | null) => void;
  /** Identifies the frame, so a change can be told from a re-render. */
  frameKey: string | null;
  /** Call when the document is ready; a jump before that is dropped. */
  onDocumentLoad: () => void;
  /** Call while rendering a page, with its intrinsic size at scale 1. */
  notePageSize: (size: { width: number; height: number } | null) => void;
  /** Call whenever the pane's zoom changes, whoever caused it. */
  noteScale: (scale: number) => void;
  /** Call when the reviewer sets the zoom themselves, so framing leaves it to them. */
  noteReviewerZoom: () => void;
}

export function useFramedRegion({
  fileUrl,
  frame,
  zoomTo,
  jumpToPage,
}: {
  fileUrl: string;
  frame: DocumentPreviewHighlight | null | undefined;
  zoomTo: (scale: number) => void;
  /** Zero-based, as the pane counts them. */
  jumpToPage: (pageIndex: number) => void;
}): FramedRegion {
  // The page to land on once the document is ready. A jump issued before the
  // pages exist is dropped, and the reader stays on page 1 of a bundle whose
  // manifest is on page 2 — the case this exists for.
  const loadedRef = useRef(false);
  const targetPage = frame?.page ?? null;

  /**
   * The framed region, identified by value rather than by object.
   *
   * `frame` is rebuilt whenever focus moves, so depending on it directly re-ran
   * the framing effect for every field the reviewer visited — including the
   * majority landing on a region identical to the last, because most of this
   * form is full-width bands shared by several boxes.
   */
  const regionX = frame?.region?.x ?? null;
  const regionY = frame?.region?.y ?? null;
  const regionWidth = frame?.region?.width ?? null;
  const regionHeight = frame?.region?.height ?? null;
  const frameKey =
    targetPage == null || regionX == null
      ? null
      : `${targetPage}:${regionX}:${regionY}:${regionWidth}:${regionHeight}`;

  const frameRegion: BoxRegion | null = useMemo(() => {
    if (regionX == null || regionY == null || regionWidth == null || regionHeight == null) {
      return null;
    }
    return { x: regionX, y: regionY, width: regionWidth, height: regionHeight };
  }, [regionX, regionY, regionWidth, regionHeight]);

  const outline = useCentredOutline(frameKey);

  const viewportRef = useRef<HTMLDivElement | null>(null);
  /** The page at scale 1, captured while rendering it. */
  const pageSizeRef = useRef<{ width: number; height: number } | null>(null);
  /**
   * The last scale we set ourselves.
   *
   * How we tell our own zoom from the reviewer's. If the current scale is not the
   * one we left, they have zoomed since, and following the caret must not
   * overrule that — help that undoes a deliberate action is not help.
   */
  const ourScaleRef = useRef<number | null>(null);
  const currentScaleRef = useRef<number | null>(null);
  /** Whether the reviewer has zoomed since we last did. */
  const reviewerZoomedRef = useRef(false);

  const { forget } = outline;
  useEffect(() => {
    loadedRef.current = false;
    ourScaleRef.current = null;
    reviewerZoomedRef.current = false;
    pageSizeRef.current = null;
    forget();
  }, [fileUrl, forget]);

  const frameNow = useCallback(() => {
    if (!loadedRef.current || targetPage == null) return;

    const page = pageSizeRef.current;
    const viewport = viewportRef.current;
    if (frameRegion != null && page != null && viewport != null) {
      const wanted = regionZoomScale({
        region: frameRegion,
        container: { width: viewport.clientWidth, height: visibleHeight(viewport) },
        page,
      });
      const scale = zoomDecision({
        // Decided at the zoom the pane can actually show, so the scale recorded as
        // ours is the one applied, and asking for the maximum from it is a no-op.
        zoom: wanted == null ? null : { ...wanted, wanted: clampZoom(wanted.wanted) },
        current: currentScaleRef.current,
        ourLast: ourScaleRef.current,
        reviewerZoomed: reviewerZoomedRef.current,
      });
      if (scale != null) {
        ourScaleRef.current = scale;
        reviewerZoomedRef.current = false;
        // A decided scale equal to the applied one is a no-op: zoomTo fires no
        // resize, so arming the owed centre would strand it for the next,
        // unrelated resize to fire and snap the reader back.
        if (scale !== currentScaleRef.current) {
          // Centred again once this has been applied, not now — the outline is
          // still where the old scale put it until the pane re-lays out.
          outline.expectZoomCentre(frameKey);
          zoomTo(scale);
        }
      }
    }

    // The page is already rendered, so the outline is on screen to scroll to —
    // and scrolling it reaches the page as well as the position. Jumping here
    // would fight this: it lands at the page's top-left, asynchronously, so its
    // scroll can arrive after ours and undo it.
    if (outline.isMounted()) {
      outline.centre(frameKey);
      return;
    }

    // Nothing to scroll to yet: the pages are not laid out, or the frame is on
    // a page this document lacks. Jump in case the page is there, and the
    // outline centres itself when it mounts.
    jumpToPage(targetPage - 1);
  }, [targetPage, frameRegion, frameKey, outline, zoomTo, jumpToPage]);

  useEffect(frameNow, [frameNow]);

  return {
    viewportRef,
    attachFramedOutline: outline.attach,
    frameKey,
    onDocumentLoad: useCallback(() => {
      loadedRef.current = true;
      frameNow();
    }, [frameNow]),
    notePageSize: useCallback((size: { width: number; height: number } | null) => {
      pageSizeRef.current = size;
    }, []),
    noteScale: useCallback((scale: number) => {
      currentScaleRef.current = scale;
    }, []),
    noteReviewerZoom: useCallback(() => {
      reviewerZoomedRef.current = true;
    }, []),
  };
}
